import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Colors, Fonts, Radii, Shadows, StatusTints, Typography } from '../constants';
import { Button } from './Button';
import { ReportReason } from '../services/moderation';

interface ReportModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (reason: ReportReason, details: string) => void;
  itemTitle: string;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  visible,
  onClose,
  onSubmit,
  itemTitle
}) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');

  const reasons: { value: ReportReason; label: string }[] = [
    { value: 'inappropriate', label: t('screens.itemDetails.reportReasons.inappropriate') },
    { value: 'scam', label: t('screens.itemDetails.reportReasons.scam') },
    { value: 'copyright', label: t('screens.itemDetails.reportReasons.copyright') },
    { value: 'other', label: t('screens.itemDetails.reportReasons.other') }
  ];

  const handleSubmit = () => {
    if (!selectedReason) return;
    onSubmit(selectedReason, details.trim());
    // Reset state
    setSelectedReason(null);
    setDetails('');
  };

  const handleClose = () => {
    setSelectedReason(null);
    setDetails('');
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {t('screens.itemDetails.reportItem')}
            </Text>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalBody}>
            <Text style={styles.itemTitleText}>
              {t('screens.itemDetails.reportingItem')}: "{itemTitle}"
            </Text>

            <Text style={styles.sectionTitle}>
              {t('screens.itemDetails.selectReason')}
            </Text>

            {reasons.map((reason) => (
              <TouchableOpacity
                key={reason.value}
                style={[
                  styles.reasonOption,
                  selectedReason === reason.value && styles.reasonOptionSelected
                ]}
                onPress={() => setSelectedReason(reason.value)}
              >
                <View style={[
                  styles.radioCircle,
                  selectedReason === reason.value && styles.radioCircleSelected
                ]}>
                  {selectedReason === reason.value && (
                    <View style={styles.radioCircleInner} />
                  )}
                </View>
                <Text style={[
                  styles.reasonText,
                  selectedReason === reason.value && styles.reasonTextSelected
                ]}>
                  {reason.label}
                </Text>
              </TouchableOpacity>
            ))}

            <Text style={styles.sectionTitle}>
              {t('screens.itemDetails.additionalDetails')} {t('common.optional')}
            </Text>
            <TextInput
              style={styles.textInput}
              multiline
              numberOfLines={4}
              value={details}
              onChangeText={setDetails}
              placeholder={t('screens.itemDetails.detailsPlaceholder')}
              placeholderTextColor={Colors.textLight}
              maxLength={500}
            />
            <Text style={styles.characterCount}>
              {details.length}/500
            </Text>
          </ScrollView>

          <View style={styles.modalFooter}>
            <Button
              title={t('common.buttons.cancel')}
              onPress={handleClose}
              variant="secondary"
              style={[styles.footerButton, styles.cancelButton]}
            />
            <Button
              title={t('common.buttons.submit')}
              onPress={handleSubmit}
              variant="danger"
              style={styles.footerButton}
              disabled={!selectedReason}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: Colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16
  },
  modalContent: {
    backgroundColor: Colors.white,
    borderRadius: Radii.dialog,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    ...Shadows.panel
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight
  },
  modalTitle: {
    ...Typography.section,
    color: Colors.textDark
  },
  closeButton: {
    padding: 4
  },
  closeButtonText: {
    fontSize: 22,
    lineHeight: 24,
    color: Colors.textLight
  },
  modalBody: {
    padding: 16
  },
  itemTitleText: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.textLight,
    marginBottom: 16
  },
  sectionTitle: {
    ...Typography.label,
    color: Colors.textDark,
    marginBottom: 10
  },
  reasonOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    backgroundColor: Colors.white,
    borderRadius: Radii.control,
    marginBottom: 8
  },
  reasonOptionSelected: {
    borderColor: Colors.error,
    backgroundColor: StatusTints.error.bg
  },
  radioCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: Colors.border,
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center'
  },
  radioCircleSelected: {
    borderColor: Colors.error
  },
  radioCircleInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: Colors.error
  },
  reasonText: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    color: Colors.text,
    flex: 1
  },
  reasonTextSelected: {
    fontFamily: Fonts.semiBold
  },
  textInput: {
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
    padding: 12,
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
    textAlignVertical: 'top',
    minHeight: 100
  },
  characterCount: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textLight,
    textAlign: 'right',
    marginTop: 4
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.borderLight,
    gap: 12
  },
  footerButton: {
    flex: 1
  },
  cancelButton: {
    borderColor: Colors.borderLight
  }
});
